import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Save, Key, Palette, Bot, Eye, EyeOff, CheckCircle, AlertCircle, Settings, BarChart3, Users, Database, Clock, RefreshCw } from 'lucide-react'
import Card from '@components/ui/Card'
import Button from '@components/ui/Button'
import Input from '@components/ui/Input'
import { useToast } from '@hooks/useToast'
import { initializeOpenAI, getApiKeyStatus } from '@services/openai'
import { getSetting, upsertSetting } from '@services/settingsService'
import { getCacheConfigs, updateCacheConfig, getCacheStats, clearCache } from '@services/cacheService'
import AuditPage from '@pages/AuditPage'
import UserManagement from '@components/settings/UserManagement'
import EventManagement from '@components/settings/EventManagement'

export default function SettingsPage() {
  const toast = useToast()
  const [activeTab, setActiveTab] = useState('general')
  const location = useLocation()
  const navigate = useNavigate()
  
  // OpenAI Settings
  const [apiKey, setApiKey] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)
  const [apiKeyStatus, setApiKeyStatus] = useState({ isConfigured: false, source: null })
  const [selectedModel, setSelectedModel] = useState('gpt-4o-mini')
  const [floatingPrompt, setFloatingPrompt] = useState('')
  
  // Appearance Settings
  const [primaryColor, setPrimaryColor] = useState('#6107F3')
  const [secondaryColor, setSecondaryColor] = useState('#6F7B83')
  const [accentColor, setAccentColor] = useState('#007BFF')
  
  // System Settings
  const [maskSensitiveData, setMaskSensitiveData] = useState(true)
  const [autoSave, setAutoSave] = useState(true)
  const [cacheEnabled, setCacheEnabled] = useState(true)
  
  // Cache Settings
  const [cacheConfigs, setCacheConfigs] = useState([])
  const [cacheStats, setCacheStats] = useState(null)
  const [loadingCache, setLoadingCache] = useState(false)
  const [loadingStats, setLoadingStats] = useState(false)

  useEffect(() => {
    loadSettings()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const tab = params.get('tab')

    if (tab && ['general', 'ai-audit', 'users', 'profiles', 'events', 'cache'].includes(tab)) {
      setActiveTab(tab)
      if (tab === 'cache') {
        loadCacheConfigs()
        loadCacheStats()
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  const loadSettings = () => {
    // Status local e tentativa de carregar do backend
    const status = getApiKeyStatus()
    setApiKeyStatus(status)

    ;(async () => {
      try {
        const openaiSet = await getSetting('openai')
        if (openaiSet?.value) {
          if (openaiSet.value.apiKey) setApiKey(openaiSet.value.apiKey)
          if (openaiSet.value.model) setSelectedModel(openaiSet.value.model)
        } else {
          const savedKey = localStorage.getItem('bravo-sim-openai-key') || localStorage.getItem('bravo-tax-openai-key')
          if (savedKey) setApiKey(savedKey)
          const savedModel = localStorage.getItem('bravo-sim-ai-model') || localStorage.getItem('bravo-tax-ai-model')
          if (savedModel) setSelectedModel(savedModel)
        }
      } catch {}

      try {
        const promptSet = await getSetting('openai_prompt')
        if (promptSet?.value?.prompt) setFloatingPrompt(promptSet.value.prompt)
        else {
          const savedPrompt = localStorage.getItem('bravo-sim-floating-system-prompt') || localStorage.getItem('bravo-tax-floating-system-prompt')
          if (savedPrompt) setFloatingPrompt(savedPrompt)
          else setFloatingPrompt(`# IDENTIDADE E PROPÓSITO
Você é um assistente especializado EXCLUSIVAMENTE no sistema BravoSim - um simulador de folha de pagamento para o setor público brasileiro.

# GUARDRAIL PRINCIPAL - ESCOPO RESTRITO
⚠️ REGRA ABSOLUTA: Responda APENAS sobre tópicos relacionados ao BravoSim e folha de pagamento pública.

## ❌ PROIBIDO RESPONDER SOBRE:
- Receitas culinárias, gastronomia ou qualquer assunto relacionado a comida
- História geral (eventos históricos, Copa do Mundo, guerras, etc.)
- Entretenimento (filmes, séries, música, jogos, celebridades)
- Ciência ou tecnologia fora do contexto do sistema
- Política, religião ou tópicos controversos não relacionados a RH público
- Programação, código ou desenvolvimento de software
- Saúde, medicina ou aconselhamento pessoal
- Qualquer outro assunto que não esteja no escopo permitido abaixo

## ✅ PERMITIDO RESPONDER SOBRE:
- Funcionalidades do sistema BravoSim (cenários, simulações, regras, análises)
- Folha de pagamento e remuneração no setor público brasileiro
- Legislação trabalhista aplicável ao serviço público (CLT adaptada, estatutos)
- Legislação previdenciária (RGPS, RPPS, PSS)
- Lei de Responsabilidade Fiscal (LRF) e limites prudenciais
- Gestão de Recursos Humanos no setor público
- Tributos sobre folha (INSS patronal, FGTS, IRRF, etc.)
- Eventos da folha de pagamento (provisões, gratificações, férias, 13º)
- Como usar o sistema: criar cenários, aplicar regras, visualizar relatórios

# COMPORTAMENTO QUANDO RECEBER PERGUNTA FORA DO ESCOPO
Se a pergunta NÃO for sobre os tópicos permitidos, responda EXATAMENTE assim:

"❌ Desculpe, não posso responder sobre esse assunto. Sou especialista exclusivamente em BravoSim e folha de pagamento do setor público.

Como posso ajudá-lo com:
• Simulações de cenários
• Regras de folha de pagamento
• Análise de impactos fiscais
• Legislação aplicável ao setor público?"

# TOM E ESTILO
- Seja direto, preciso e profissional
- Use linguagem técnica quando apropriado, mas explique termos complexos
- Forneça exemplos práticos quando possível
- Cite a legislação relevante quando aplicável
- Seja conciso - respostas curtas são melhores que textos longos

# LIMITAÇÕES
- Não invente dados ou simulações - oriente o usuário a usar as funcionalidades do sistema
- Não faça cálculos complexos manualmente - sugira usar o simulador
- Não dê consultoria jurídica definitiva - sempre recomende consultar especialistas

# FUNCIONALIDADES DO SISTEMA QUE VOCÊ PODE AJUDAR A USAR
- Criação e edição de cenários de simulação
- Aplicação de regras de folha de pagamento
- Análise de impactos orçamentários
- Geração de relatórios e exportações
- Configurações do sistema

NUNCA desvie deste escopo. SEMPRE recuse educadamente perguntas fora do domínio permitido.`)
        }
      } catch {}

      try {
        const colorsSet = await getSetting('ui_colors')
        if (colorsSet?.value) {
          const colors = colorsSet.value
          setPrimaryColor(colors.primary || '#6107F3')
          setSecondaryColor(colors.secondary || '#6F7B83')
          setAccentColor(colors.accent || '#007BFF')
        } else {
          const savedColors = localStorage.getItem('bravo-sim-colors') || localStorage.getItem('bravo-tax-colors')
          if (savedColors) {
            const colors = JSON.parse(savedColors)
            setPrimaryColor(colors.primary || '#6107F3')
            setSecondaryColor(colors.secondary || '#6F7B83')
            setAccentColor(colors.accent || '#007BFF')
          }
        }
      } catch {}

      try {
        const sysSet = await getSetting('ui_system')
        if (sysSet?.value) {
          setMaskSensitiveData(!!sysSet.value.maskSensitiveData)
          setAutoSave(!!sysSet.value.autoSave)
          setCacheEnabled(!!sysSet.value.cacheEnabled)
        } else {
          const savedMask = localStorage.getItem('bravo-sim-mask-sensitive') || localStorage.getItem('bravo-tax-mask-sensitive')
          if (savedMask !== null) setMaskSensitiveData(savedMask === 'true')
          const savedAutoSave = localStorage.getItem('bravo-sim-auto-save') || localStorage.getItem('bravo-tax-auto-save')
          if (savedAutoSave !== null) setAutoSave(savedAutoSave === 'true')
          const savedCache = localStorage.getItem('bravo-sim-cache-enabled') || localStorage.getItem('bravo-tax-cache-enabled')
          if (savedCache !== null) setCacheEnabled(savedCache === 'true')
        }
      } catch {}

      // Load cache configurations
      loadCacheConfigs()
    })()
  }
  
  const handleSaveOpenAI = () => {
    if (!apiKey.trim()) {
      toast.error('Por favor, insira uma API Key válida')
      return
    }

    try {
      initializeOpenAI(apiKey.trim())
      upsertSetting('openai', { apiKey: apiKey.trim(), model: selectedModel }).catch(()=>{})
      localStorage.setItem('bravo-sim-ai-model', selectedModel)
      localStorage.removeItem('bravo-tax-ai-model')
      setApiKeyStatus({ isConfigured: true, source: 'db' })

      // Notifica outros componentes que a OpenAI foi configurada
      window.dispatchEvent(new Event('openai-configured'))

      toast.success('Configurações OpenAI salvas com sucesso ✓')
    } catch (error) {
      toast.error('Erro ao salvar: ' + error.message)
    }
  }

  const handleSaveAgentPrompt = () => {
    upsertSetting('openai_prompt', { prompt: floatingPrompt || '' }).catch(()=>{})
    localStorage.setItem('bravo-sim-floating-system-prompt', floatingPrompt || '')
    localStorage.removeItem('bravo-tax-floating-system-prompt')
    toast.success('Prompt do agente flutuante salvo ✓')
  }
  
  const handleSaveAppearance = () => {
    const colors = {
      primary: primaryColor,
      secondary: secondaryColor,
      accent: accentColor,
    }
    upsertSetting('ui_colors', colors).catch(()=>{})
    localStorage.setItem('bravo-sim-colors', JSON.stringify(colors))
    localStorage.removeItem('bravo-tax-colors')
    
    // Update CSS variables
    document.documentElement.style.setProperty('--color-primary', primaryColor)
    document.documentElement.style.setProperty('--color-secondary', secondaryColor)
    document.documentElement.style.setProperty('--color-accent', accentColor)
    
    toast.success('Aparência personalizada salva! Recarregue a página para aplicar.')
  }
  
  const handleSaveSystem = () => {
    upsertSetting('ui_system', { maskSensitiveData, autoSave, cacheEnabled }).catch(()=>{})
    localStorage.setItem('bravo-sim-mask-sensitive', maskSensitiveData.toString())
    localStorage.setItem('bravo-sim-auto-save', autoSave.toString())
    localStorage.setItem('bravo-sim-cache-enabled', cacheEnabled.toString())
    localStorage.removeItem('bravo-tax-mask-sensitive')
    localStorage.removeItem('bravo-tax-auto-save')
    localStorage.removeItem('bravo-tax-cache-enabled')
    toast.success('Configurações do sistema salvas ✓')
  }
  
  const handleResetColors = () => {
    setPrimaryColor('#6107F3')
    setSecondaryColor('#6F7B83')
    setAccentColor('#007BFF')
    localStorage.removeItem('bravo-sim-colors')
    toast.info('Cores redefinidas para o padrão')
  }

  const loadCacheConfigs = async () => {
    try {
      setLoadingCache(true)
      const configs = await getCacheConfigs()
      setCacheConfigs(Array.isArray(configs) ? configs : [])
    } catch (error) {
      console.error('Error loading cache configs:', error)
      toast.error('Erro ao carregar configurações de cache')
    } finally {
      setLoadingCache(false)
    }
  }

  const loadCacheStats = async () => {
    try {
      setLoadingStats(true)
      const stats = await getCacheStats()
      setCacheStats(stats)
    } catch (error) {
      console.error('Error loading cache stats:', error)
      toast.error('Erro ao carregar estatísticas de cache')
    } finally {
      setLoadingStats(false)
    }
  }

  const handleCacheToggle = async (configId, enabled) => {
    try {
      await updateCacheConfig(configId, { enabled })
      setCacheConfigs(prev => prev.map(config => 
        config.id === configId ? { ...config, enabled } : config
      ))
      toast.success('Configuração de cache atualizada')
    } catch (error) {
      console.error('Error updating cache config:', error)
      toast.error('Erro ao atualizar configuração de cache')
    }
  }

  const handleCacheTTLChange = async (configId, ttl) => {
    try {
      await updateCacheConfig(configId, { ttl: parseInt(ttl) })
      setCacheConfigs(prev => prev.map(config => 
        config.id === configId ? { ...config, ttl: parseInt(ttl) } : config
      ))
      toast.success('TTL do cache atualizado')
    } catch (error) {
      console.error('Error updating cache TTL:', error)
      toast.error('Erro ao atualizar TTL do cache')
    }
  }

  const handleClearCache = async (cacheType) => {
    try {
      await clearCache(cacheType)
      toast.success(`Cache ${cacheType} limpo com sucesso`)
      // Reload cache stats if available
      loadCacheConfigs()
    } catch (error) {
      console.error('Error clearing cache:', error)
      toast.error('Erro ao limpar cache')
    }
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    const params = new URLSearchParams(location.search)
    params.set('tab', tab)
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true })
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          Configurações
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Personalize o sistema e integre com OpenAI
        </p>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        <nav className="flex space-x-4 sm:space-x-8 min-w-max px-1">
          <button
            onClick={() => handleTabChange('general')}
            className={`py-2 px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'general'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Geral</span>
            </div>
          </button>
          <button
            onClick={() => handleTabChange('ai-audit')}
            className={`py-2 px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'ai-audit'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-1.5 sm:gap-2">
              <BarChart3 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Auditoria IA</span>
            </div>
          </button>
          <button
            onClick={() => handleTabChange('users')}
            className={`py-2 px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'users'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Usuários</span>
            </div>
          </button>
          <button
            onClick={() => handleTabChange('profiles')}
            className={`py-2 px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'profiles'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Perfis</span>
            </div>
          </button>
          <button
            onClick={() => handleTabChange('events')}
            className={`py-2 px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'events'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-1.5 sm:gap-2">
              <BarChart3 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Eventos</span>
            </div>
          </button>
          <button
            onClick={() => handleTabChange('cache')}
            className={`py-2 px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'cache'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Database className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Cache</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'general' && (
        <div className="max-w-5xl mx-auto space-y-6">
          {/* OpenAI Configuration */}
          <Card title="Integração OpenAI" icon={Bot}>
            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
                {apiKeyStatus.isConfigured ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      OpenAI configurada ({apiKeyStatus.source === 'env' ? '.env' : 'localStorage'})
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-5 w-5 text-yellow-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      OpenAI não configurada - Configure abaixo
                    </span>
                  </>
                )}
              </div>

              {/* API Key */}
              <div className="relative">
                <Input
                  label="API Key OpenAI"
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="sk-proj-..."
                  leftIcon={Key}
                />
                <button
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-9 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  {showApiKey ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              {/* Model Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Modelo de IA Padrão
                </label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="gpt-4o">GPT-4o (mais inteligente, mais caro)</option>
                  <option value="gpt-4o-mini">GPT-4o-mini (mais rápido, mais barato)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedModel === 'gpt-4o'
                    ? 'Input: $0.005/1K tokens | Output: $0.015/1K tokens'
                    : 'Input: $0.00015/1K tokens | Output: $0.0006/1K tokens'}
                </p>
              </div>

              <Button onClick={handleSaveOpenAI} variant="primary">
                <Save className="h-4 w-4 mr-2" />
                Salvar Configurações OpenAI
              </Button>
            </div>
          </Card>

          {/* Floating Agent Prompt */}
          <Card title="Agente Flutuante (IA)" icon={Bot}>
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Prompt padrão do agente
              </label>
              <textarea
                value={floatingPrompt}
                onChange={(e) => setFloatingPrompt(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Defina o comportamento padrão do agente flutuante..."
              />
              <div className="flex gap-3">
                <Button onClick={handleSaveAgentPrompt} variant="primary">
                  <Save className="h-4 w-4 mr-2" />
                  Salvar Prompt do Agente
                </Button>
              </div>
            </div>
          </Card>

          {/* Appearance */}
          <Card title="Aparência" icon={Palette}>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Primary Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cor Primária
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="w-16 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Secondary Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cor Secundária
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={secondaryColor}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                      className="w-16 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={secondaryColor}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Accent Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cor de Destaque
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={accentColor}
                      onChange={(e) => setAccentColor(e.target.value)}
                      className="w-16 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={accentColor}
                      onChange={(e) => setAccentColor(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={handleSaveAppearance} variant="primary">
                  <Save className="h-4 w-4 mr-2" />
                  Salvar Aparência
                </Button>
                <Button onClick={handleResetColors} variant="outline">
                  Restaurar Padrão
                </Button>
              </div>
            </div>
          </Card>

          {/* System Settings */}
          <Card title="Sistema">
            <div className="space-y-4">
              {/* Mask Sensitive Data */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">
                    Mascarar Dados Sensíveis
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Ocultar CPF, CNPJ e outros dados sensíveis nas listagens
                  </p>
                </div>
                <button
                  onClick={() => setMaskSensitiveData(!maskSensitiveData)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    maskSensitiveData ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      maskSensitiveData ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Auto Save */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">
                    Salvamento Automático
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Salvar alterações automaticamente ao editar
                  </p>
                </div>
                <button
                  onClick={() => setAutoSave(!autoSave)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    autoSave ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      autoSave ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Cache */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">
                    Cache de Respostas IA
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Reutilizar respostas recentes para economizar tokens
                  </p>
                </div>
                <button
                  onClick={() => setCacheEnabled(!cacheEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    cacheEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      cacheEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <Button onClick={handleSaveSystem} variant="primary">
                <Save className="h-4 w-4 mr-2" />
                Salvar Configurações do Sistema
              </Button>
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'ai-audit' && <AuditPage />}
      {activeTab === 'users' && <UserManagement section="users" />}
      {activeTab === 'profiles' && <UserManagement section="profiles" />}
      {activeTab === 'events' && <EventManagement />}
      
      {activeTab === 'cache' && (
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Cache Overview */}
          <Card title="Visão Geral do Cache" icon={Database}>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Database className="h-5 w-5 text-blue-600" />
                    <span className="font-medium text-blue-900 dark:text-blue-100">Redis</span>
                  </div>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Cache em memória para alta performance
                  </p>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-900 dark:text-green-100">TTL Configurável</span>
                  </div>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    Controle de expiração por tipo de cache
                  </p>
                </div>
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="h-5 w-5 text-purple-600" />
                    <span className="font-medium text-purple-900 dark:text-purple-100">Invalidation</span>
                  </div>
                  <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                    Limpeza automática e manual
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Cache Configurations */}
          <Card title="Configurações de Cache" icon={Settings}>
            {loadingCache ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-2 text-gray-600 dark:text-gray-400">Carregando configurações...</span>
              </div>
            ) : (
              <div className="space-y-4">
                {Array.isArray(cacheConfigs) && cacheConfigs.length > 0 ? (
                  cacheConfigs.map((config) => (
                    <div key={config.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                            {config.cache_type.replace('_', ' ')}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {config.cache_type === 'dashboard_summary' && 'Cache para resumos do dashboard'}
                            {config.cache_type === 'person_summary' && 'Cache para resumos por pessoa'}
                            {config.cache_type === 'trends' && 'Cache para dados de tendências'}
                            {config.cache_type === 'distincts' && 'Cache para valores distintos'}
                            {config.cache_type === 'simulations' && 'Cache para simulações'}
                          </p>
                        </div>
                        <button
                          onClick={() => handleCacheToggle(config.id, !config.enabled)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            config.enabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              config.enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            TTL (segundos)
                          </label>
                          <input
                            type="number"
                            value={config.ttl}
                            onChange={(e) => handleCacheTTLChange(config.id, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            min="60"
                            max="86400"
                          />
                        </div>
                        <div className="flex items-end">
                          <Button
                            onClick={() => handleClearCache(config.cache_type)}
                            variant="outline"
                            size="sm"
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Limpar
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <p className="mb-4">Nenhuma configuração de cache foi encontrada.</p>
                    <Button onClick={loadCacheConfigs} variant="outline">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Tentar Novamente
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Cache Statistics */}
          <Card 
            title="Estatísticas do Cache" 
            icon={BarChart3}
            actions={
              <Button onClick={loadCacheStats} variant="ghost" size="sm">
                <RefreshCw className={`h-4 w-4 ${loadingStats ? 'animate-spin' : ''}`} />
              </Button>
            }
          >
            {loadingStats ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-2 text-gray-600 dark:text-gray-400">Carregando estatísticas...</span>
              </div>
            ) : cacheStats && cacheStats.backend ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Hits vs Misses</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Taxa de acerto do cache
                    </p>
                    <div className="mt-2 text-2xl font-bold text-green-600">
                      {cacheStats.hit_ratio}%
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-green-500">Hits: {cacheStats.hits}</span>
                      <span className="text-red-500">Misses: {cacheStats.misses}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Performance</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Uso de memória e chaves
                    </p>
                    <div className="mt-2 text-2xl font-bold text-blue-600">
                      {cacheStats.memory_usage}
                    </div>
                     <div className="flex justify-between text-sm mt-1">
                      <span className="text-gray-500">Keys: {cacheStats.keys}</span>
                      <span className="text-gray-500 capitalize">Backend: {cacheStats.backend}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Não foi possível carregar as estatísticas do cache. <br />
                Verifique se o Redis está em execução e configurado corretamente no backend.
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  )
}
